import {otsBaseApi} from "../../ots-api";
import {Trailer} from "../../../model/trailer";
import {TrailerContents} from "../../../model/trailer-contents";
import {removeUndefined} from "../../../util/util";
import {FleetAssetCapability, FleetAssetCapabilityRequestDTO} from "../../../model/fleet-asset-capability";

const ALL_TAG_TYPES = [
  "Trailers",
  "TrailerContents",
  "TrailerCapabilities",
  "ObjectObjectChangeLogs"
] as const;

type TagType = typeof ALL_TAG_TYPES[number];

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: ALL_TAG_TYPES
});

const trailerApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllTrailers: builder.query<Trailer[], string>({
      query: (regionId) => ({
        url: `/trailer/all`,
        method: "GET",
        params: removeUndefined({
          regionId: regionId,
          withState: true
        })
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Trailers", id} as const)),
            {type: "Trailers", id: "LIST"}
          ];
        } else {
          return [{type: "Trailers", id: "LIST"}];
        }
      }
    }),
    getAllOperatorTrailers: builder.query<Trailer[], string>({
      query: (regionId) => ({
        url: `/trailer/all/operator`,
        method: "GET",
        params: removeUndefined({
          regionId: regionId,
          withState: true
        })
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Trailers", id} as const)),
            {type: "Trailers", id: "LIST"}
          ];
        } else {
          return [{type: "Trailers", id: "LIST"}];
        }
      }
    }),
    getTrailer: builder.query<Trailer, string>({
      query: (id) => ({
        url: `/trailer/${id}`,
        method: "GET"
      }),
      providesTags: (result, error, id) => [{type: "Trailers", id}]
    }),
    getTrailerContents: builder.query<TrailerContents, { id: string, service?: string }>({
      query: (args) => ({
        url: `/trailer/${args.id}/contents`,
        method: "GET",
        params: args.service ? {service: args.service} : undefined
      }),
      keepUnusedDataFor: 60,
      providesTags: (result, error, arg) => [{type: "TrailerContents", id: arg.id}]
    }),
    createTrailer: builder.mutation<Trailer, Partial<Trailer>>({
      query: (trailer) => ({
        url: '/trailer',
        method: 'POST',
        body: trailer,
      }),
      invalidatesTags: [{type: 'Trailers', id: 'LIST'}],
    }),
    updateTrailer: builder.mutation<Trailer, Partial<Trailer>>({
      query: (trailer) => ({
        url: '/trailer',
        method: 'PUT',
        body: trailer,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: 'Trailers', id},
        {type: 'ObjectObjectChangeLogs', id}
      ],
    }),
    deleteTrailer: builder.mutation<void, Partial<Trailer>>({
      query: (trailer) => ({
        url: '/trailer',
        method: 'DELETE',
        body: trailer,
      }),
      invalidatesTags: (result, error, {id}) => [{type: 'Trailers', id}],
    }),
    getTrailerCapabilities: builder.query<FleetAssetCapability[], string>({
      query: (trailerId) => ({
        url: `/trailers/${trailerId}/capabilities`,
        method: "GET",
      }),
      providesTags: (result, error, arg) => {
        return [
          {type: "TrailerCapabilities", id: arg}
        ];
      }
    }),
    addCapabilitiesToTrailer: builder.mutation<void, { trailerId: string, data: FleetAssetCapabilityRequestDTO }>({
      query: (arg) => ({
        url: `/trailers/${arg.trailerId}/capabilities`,
        method: "POST",
        body: arg.data,
      }),
      invalidatesTags: (result, error, {trailerId}) => [{type: "TrailerCapabilities", id: trailerId}]
    }),
    removeCapabilitiesFromTrailer: builder.mutation<void, { trailerId: string, data: FleetAssetCapabilityRequestDTO }>({
      query: (arg) => ({
        url: `/trailers/${arg.trailerId}/capabilities`,
        method: "DELETE",
        body: arg.data,
      }),
      invalidatesTags: (result, error, {trailerId}) => [{type: "TrailerCapabilities", id: trailerId}]
    }),
  })
});

export const {
  useGetAllTrailersQuery,
  useGetAllOperatorTrailersQuery,
  useGetTrailerQuery,
  useGetTrailerContentsQuery,
  useCreateTrailerMutation,
  useUpdateTrailerMutation,
  useDeleteTrailerMutation,
  useGetTrailerCapabilitiesQuery,
  useAddCapabilitiesToTrailerMutation,
  useRemoveCapabilitiesFromTrailerMutation,
} = trailerApi;
